import type { RealestateAd, RealestateProjectAd } from '../../types';
import { formatNumber } from '../lib/formatNumber';

export function getHeading(
    ad: RealestateAd | RealestateProjectAd,
    isLettingsOrLettingsWanted = false,
): string {
    let output: string[] = [];
    if (ad.local_area_name && !isLettingsOrLettingsWanted)
        output = output.concat(ad.local_area_name);
    if (ad.heading) output = output.concat(ad.heading);
    return output.join(' - ');
}

export function getHighlightColor(
    ad: RealestateAd | RealestateProjectAd,
): string {
    const { styling } = ad;

    const highlightColorItem = styling?.find((s) =>
        s.startsWith('highlight_colour:'),
    );

    if (highlightColorItem) {
        const colorParts = highlightColorItem.split(':');
        if (colorParts.length === 2) {
            return `${colorParts[1]}`;
        }
    }

    return '#C3CCD9';
}

export function getAreaText(ad: RealestateAd | RealestateProjectAd): string {
    if (isRealestateProjectAd(ad)) {
        return '';
    }

    if (ad.area && ad.area.size > 0) {
        return formatNumber(ad.area.size) + ' ' + ad.area.unit;
    } else if (ad.area_range) {
        return getAreaRange(ad);
    } else if (ad.area_plot && ad.area_plot.size > 0) {
        return formatNumber(ad.area_plot.size) + ' ' + ad.area_plot.unit;
    } else {
        return '';
    }
}

export function getAreaRange(ad: RealestateAd): string {
    if (!ad.area_range) {
        return '';
    }

    let output: string[] = [];
    if (ad.area_range.size_from)
        output = output.concat(formatNumber(ad.area_range.size_from));
    if (
        ad.area_range.size_to &&
        ad.area_range.size_to !== ad.area_range.size_from
    )
        output = output.concat(formatNumber(ad.area_range.size_to));
    return output.length > 0
        ? output.join(' - ') + ' ' + ad.area_range.unit
        : '';
}

export function getPrice(ad: RealestateAd | RealestateProjectAd): string {
    if (isRealestateProjectAd(ad)) {
        return '';
    }

    if (ad.price_suggestion && ad.price_suggestion.amount > 0) {
        return formatNumber(ad.price_suggestion.amount) + '\u00A0kr';
    } else {
        return '';
    }
}

export function getPriceRangeSuggestion(
    ad: RealestateAd | RealestateProjectAd,
): string {
    let output: string[] = [];

    if (isRealestateProjectAd(ad)) {
        if (!ad.price_range_suggestion) {
            return '';
        }
        if (ad.price_range_suggestion.amount_from)
            output = output.concat(
                formatNumber(ad.price_range_suggestion.amount_from),
            );
        if (
            ad.price_range_suggestion.amount_to &&
            ad.price_range_suggestion.amount_to !==
                ad.price_range_suggestion.amount_from
        )
            output = output.concat(
                formatNumber(ad.price_range_suggestion.amount_to),
            );
    }

    return output.length > 0 ? output.join(' - ') + '\u00A0kr' : '';
}

export function getPriceBulletPointsText(
    ad: RealestateAd | RealestateProjectAd,
    isLettings = false,
): string {
    let output: string[] = [];

    if (isRealestateProjectAd(ad)) {
        if (
            ad.price_range_total &&
            ad.price_range_total.amount_from &&
            ad.price_range_total.amount_to
        )
            output = output.concat(
                'Totalpris: ' +
                    formatNumber(ad.price_range_total.amount_from) +
                    ' - ' +
                    formatNumber(ad.price_range_total.amount_to) +
                    '\u00A0kr',
            );
    } else {
        if (ad.price_total && ad.price_total.amount > 0 && !isLettings)
            output = output.concat(
                'Totalpris: ' +
                    formatNumber(ad.price_total.amount) +
                    '\u00A0kr',
            );
        if (ad.price_shared_cost && ad.price_shared_cost.amount > 0)
            output = output.concat(
                'Fellesutg.: ' +
                    formatNumber(ad.price_shared_cost.amount) +
                    '\u00A0kr',
            );
    }

    return output.join(' ∙ ');
}

export function getAreaBulletPointsText(
    ad: RealestateAd | RealestateProjectAd,
): string {
    let output: string[] = [];

    if (ad.owner_type_description)
        output = output.concat(ad.owner_type_description);
    if (
        ad.property_type_description &&
        'Tomter' !== ad.property_type_description &&
        'Hyttetomt' !== ad.property_type_description &&
        'Næringstomt' !== ad.property_type_description
    )
        output = output.concat(ad.property_type_description);

    if (!isRealestateProjectAd(ad)) {
        if (ad.number_of_bedrooms)
            output = output.concat(String(ad.number_of_bedrooms) + ' soverom');
    }

    return output.join(' ∙ ');
}

function isRealestateProjectAd(
    ad: RealestateAd | RealestateProjectAd,
): ad is RealestateProjectAd {
    return ad.type === 'realestate_project';
}
